import type { Ring, Vec2, Vec3 } from '../../../../geo/types';
import { octahedronSolid, orientedFace, planPoint } from '../../solids';
import { panelWall, pointedArchRise, pointedWindow } from '../../panels';
import { edgePanel, openedWall, rectWall, roofUv } from '../masonry';
import { pinnacleSolid } from '../gothic/pinnacle';
import { plainWall } from '../wallCoords';
import { LANDMARK_PATTERN, patternedWalls } from '../surfacePatterns';
import type { OudeKerkBuild } from './oudeKerkParts';

/**
 * The raised middle vessel of 1510-12 as one closed shell: on every bay a gable to each side with its
 * lancet, the transverse roofs behind the gables and, between them, the long roof they cut into at
 * equal height (Janse 2004, section 1.11). The shell is the union of the long roof and the transverse
 * roofs, so no face lies inside another. Its masonry above the aisle roofs is banded with stone
 * (section 1.11), and sandstone copings with crockets and a finial crowned every gable (Janse, after
 * the Loterijprent of 1558); their size is not recorded.
 */
const GABLE = {
  crocket: { every: 1.1, size: 0.14, lift: 0.1 },
  finial: { half: 0.2, below: 0.6, shaft: 0.6, tip: 1.6 },
} as const;

interface Section {
  readonly v0: number;
  readonly v1: number;
  readonly vc: number;
  readonly walkwayY: number;
  readonly shoulderY: number;
  readonly ridgeY: number;
}

function sectionOf(s: OudeKerkBuild): Section {
  const { nave, lichtbeuk: lb, aisles } = s.plan;
  return {
    v0: nave.v0,
    v1: nave.v1,
    vc: (nave.v0 + nave.v1) / 2,
    walkwayY: aisles[0]!.eaveY,
    shoulderY: lb.shoulderY,
    ridgeY: lb.ridgeY,
  };
}

/** A bay's gable wall from the ground, split at the walkway so the partition's edge meets it, with its lancet. */
function gableOutline(width: number, x: Section): Ring {
  return [
    [0, 0],
    [width, 0],
    [width, x.walkwayY],
    [width, x.shoulderY],
    [width / 2, x.ridgeY],
    [0, x.shoulderY],
    [0, x.walkwayY],
  ];
}

function buildGables(s: OudeKerkBuild, x: Section): void {
  const lb = s.plan.lichtbeuk;
  const w = lb.windowWidth;
  const banded = patternedWalls(s.stone, LANDMARK_PATTERN.speklagen);
  lb.bays.slice(1).forEach((b, k) => {
    const a = lb.bays[k]!;
    const width = b - a;
    const lancet = [pointedWindow(width / 2, lb.sill, w, lb.windowTop - pointedArchRise(w), 3)];
    openedWall(s.builder, banded, s.frame, edgePanel([b, x.v1], [a, x.v1]), gableOutline(width, x), lancet);
    openedWall(s.builder, banded, s.frame, edgePanel([a, x.v0], [b, x.v0]), gableOutline(width, x), lancet);
  });
}

/** A roof face, its slate courses run level along `eave` (two points on the eave line) and up to `upper`. */
function slope(
  s: OudeKerkBuild,
  points: readonly Vec3[],
  outward: Vec3,
  eave: readonly [Vec3, Vec3],
  upper: Vec3
): void {
  orientedFace(s.builder, s.stone.roof, s.frame, points, outward, roofUv(eave[0], eave[1], upper));
}

/** Each bay's transverse roof, in the halves the long roof leaves standing: triangles from the gable to the ridge. */
function buildTransverseRoofs(s: OudeKerkBuild, x: Section): void {
  const lb = s.plan.lichtbeuk;
  const p = (u: number, y: number, v: number) => planPoint(u, y, v);
  lb.bays.slice(1).forEach((b, k) => {
    const a = lb.bays[k]!;
    const m = (a + b) / 2;
    for (const edge of [x.v1, x.v0]) {
      const west = [p(a, x.shoulderY, edge), p(a, x.shoulderY, x.vc)] as const;
      const east = [p(b, x.shoulderY, edge), p(b, x.shoulderY, x.vc)] as const;
      slope(
        s,
        [p(a, x.shoulderY, edge), p(m, x.ridgeY, edge), p(m, x.ridgeY, x.vc)],
        [-1, 0.4, 0],
        west,
        p(m, x.ridgeY, edge)
      );
      slope(
        s,
        [p(b, x.shoulderY, edge), p(m, x.ridgeY, x.vc), p(m, x.ridgeY, edge)],
        [1, 0.4, 0],
        east,
        p(m, x.ridgeY, edge)
      );
    }
  });
}

/** The long roof between the transverse roofs: a triangle over every valley and one at each end. */
function buildLongRoof(s: OudeKerkBuild, x: Section): void {
  const bays = s.plan.lichtbeuk.bays;
  const mids = bays.slice(1).map((b, k) => (bays[k]! + b) / 2);
  const ridge = [bays[0]!, ...mids, bays[bays.length - 1]!];
  const p = (u: number, y: number, v: number) => planPoint(u, y, v);
  for (const [edge, out] of [
    [x.v1, -1],
    [x.v0, 1],
  ] as const) {
    bays.forEach((u, k) => {
      const west = ridge[k]!;
      const east = ridge[k + 1]!;
      const eave = [p(u, x.shoulderY, edge), p(u + 1, x.shoulderY, edge)] as const;
      slope(
        s,
        [p(u, x.shoulderY, edge), p(east, x.ridgeY, x.vc), p(west, x.ridgeY, x.vc)],
        [0, 1, out],
        eave,
        p(u, x.ridgeY, x.vc)
      );
    });
  }
}

/** The west end in banded brick behind the tower; the east end over the crossing is the timber partition (woodcut 1544). */
function buildEnds(s: OudeKerkBuild, x: Section): void {
  const bays = s.plan.lichtbeuk.bays;
  const west = bays[0]!;
  const east = bays[bays.length - 1]!;
  const width = x.v1 - x.v0;
  const head = (y0: number): Ring => [
    [0, y0],
    [width, y0],
    [width, x.shoulderY],
    [width / 2, x.ridgeY],
    [0, x.shoulderY],
  ];
  const timber = { wall: s.timber, opening: s.timber };
  plainWall(
    s.builder,
    patternedWalls(s.stone, LANDMARK_PATTERN.speklagen),
    s.frame,
    edgePanel([west, x.v1], [west, x.v0]),
    [[0, 0], [width, 0], [width, x.walkwayY], ...head(x.walkwayY).slice(2), [0, x.walkwayY]],
    []
  );
  rectWall(
    s.builder,
    s.stone,
    s.frame,
    [
      [east, x.v0],
      [east, x.v1],
    ],
    [0, x.walkwayY]
  );
  panelWall(
    s.builder,
    timber,
    s.frame,
    edgePanel([east, x.v0], [east, x.v1]),
    head(x.walkwayY),
    []
  );
}

/** Crockets along a gable's coping from its shoulder to its apex, set just proud of the edge. */
function crocketsAlong(s: OudeKerkBuild, shoulder: Vec2, apex: Vec2, v: number): void {
  const c = GABLE.crocket;
  const du = apex[0] - shoulder[0];
  const dy = apex[1] - shoulder[1];
  const length = Math.hypot(du, dy);
  const lift: Vec2 = [(-Math.sign(du) * dy * c.lift) / length, (Math.abs(du) * c.lift) / length];
  const count = Math.floor(length / c.every) - 1;
  for (let k = 1; k <= count; k++) {
    const t = (k * c.every) / length;
    const at = planPoint(shoulder[0] + du * t + lift[0], shoulder[1] + dy * t + lift[1], v);
    octahedronSolid(s.builder, s.stone.wall, s.frame, at, c.size);
  }
}

function buildCopings(s: OudeKerkBuild, x: Section): void {
  const lb = s.plan.lichtbeuk;
  const f = GABLE.finial;
  lb.bays.slice(1).forEach((b, k) => {
    const a = lb.bays[k]!;
    const m = (a + b) / 2;
    for (const v of [x.v0, x.v1]) {
      crocketsAlong(s, [a, x.shoulderY], [m, x.ridgeY], v);
      crocketsAlong(s, [b, x.shoulderY], [m, x.ridgeY], v);
      const spec = {
        center: [m, v] as Vec2,
        half: f.half,
        y0: x.ridgeY - f.below,
        shaftTop: x.ridgeY + f.shaft,
        tipY: x.ridgeY + f.tip,
      };
      pinnacleSolid(s.builder, s.stone.wall, s.frame, spec);
    }
  });
}

export function buildLichtbeuk(s: OudeKerkBuild): void {
  const x = sectionOf(s);
  buildGables(s, x);
  buildTransverseRoofs(s, x);
  buildLongRoof(s, x);
  buildEnds(s, x);
  buildCopings(s, x);
}
